import { Button } from "@dmca/frontend/components/button";
import { MutateAppPermissionsForm } from "@dmca/frontend/components/display";
import { Input } from "@dmca/frontend/components/input";
import { PageHeader } from "@dmca/frontend/components/nav";
import { checkPermissionsInLoader } from "@dmca/frontend/hooks";
import {
  deserializeAppPermissions,
  serializeAppPermissions,
  type AppPermissionIdentifierWithEffect,
  type AppPermissionResourceWithEffect,
} from "@dmca/models/interface/auth";
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Stack,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/admin/TenantPermission")({
  component: RouteComponent,
  loader: async ({ context }) =>
    await checkPermissionsInLoader("Admin::UpdateTenantPermission", context),
});

function RouteComponent() {
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const { data: tenantPermissions } = useQuery(
    trpc.auth.getTenantPermissions.queryOptions()
  );
  const [permissionString, setPermissionString] = useState(
    (tenantPermissions ?? []).join(", ")
  );
  const [rawMode, setRawMode] = useState(true);
  const form = useForm({
    defaultValues: {
      permissions: deserializeAppPermissions(tenantPermissions ?? []),
    },
  });
  const {
    watch,
    handleSubmit,
    setValue,
    reset,
    formState: { isDirty },
  } = form;

  useEffect(() => {
    const { unsubscribe } = watch((value) => {
      if (!rawMode) {
        setPermissionString(
          serializeAppPermissions(
            value.permissions as AppPermissionResourceWithEffect
          ).join(", ")
        );
      }
    });
    return () => unsubscribe();
  }, [watch, rawMode]);

  const mutation = useMutation(
    trpc.auth.updateTenantPermission.mutationOptions({
      onSuccess: () => {
        toast.success("Permissions updated successfully");
      },
    })
  );

  const onSubmit = handleSubmit(() => {
    mutation.mutate({
      permissions: permissionString.split(
        /\s*,\s*/
      ) as AppPermissionIdentifierWithEffect[],
    });
  });
  return (
    <>
      <PageHeader>Tenant Permissions</PageHeader>
      <Stack direction="row" justifyContent="end" sx={{ width: "100%" }}>
        <FormControlLabel
          control={
            <Checkbox
              value={rawMode}
              onChange={(e) => setRawMode(e.target.checked)}
              checked={rawMode}
            />
          }
          label="Raw Mode"
          labelPlacement="start"
        />
      </Stack>
      <Box
        component="form"
        onSubmit={onSubmit}
        onReset={() => {
          reset();
          setPermissionString((tenantPermissions ?? []).join(", "));
        }}
      >
        <MutateAppPermissionsForm
          form={form}
          // onChange={(value) => setValue("permissions", value.join(", "))}
          readonly={rawMode}
          showRawPermissionName
          sx={{ width: "100%", justifyContent: "space-evenly" }}
        />
        <Collapse in={rawMode}>
          <Input
            value={permissionString}
            onChange={(e) => {
              setPermissionString(e.target.value);
              setValue(
                "permissions",
                deserializeAppPermissions(
                  e.target.value.split(
                    /\s*,\s*/
                  ) as AppPermissionIdentifierWithEffect[]
                )
              );
            }}
            multiline
            label="Raw Permissions"
            minRows={3}
            maxRows={10}
            helperText="One permission per line"
          />
        </Collapse>
        <Stack direction="row" spacing={1}>
          <Button
            fullWidth
            type="submit"
            loading={mutation.isPending}
            disabled={
              !isDirty &&
              permissionString === (tenantPermissions ?? []).join(", ")
            }
          >
            Submit
          </Button>
          <Button
            fullWidth
            color="error"
            type="reset"
            disabled={
              mutation.isPending ||
              (!isDirty &&
                permissionString === (tenantPermissions ?? []).join(", "))
            }
          >
            Reset
          </Button>
        </Stack>
      </Box>
    </>
  );
}
